import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Ticket, FileSpreadsheet, LogOut, User } from 'lucide-react';
import { axiosInstance } from '@/App';

export default function ClientPortalDashboard() {
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState({
    invoices: 0,
    tickets: 0,
    quotes: 0,
    pending_quotes: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomerData();
    loadStats();
  }, []);

  const loadCustomerData = () => {
    const storage = localStorage.getItem('client_portal_user') 
      ? localStorage 
      : sessionStorage;
    const userData = storage.getItem('client_portal_user');
    
    if (userData) {
      setCustomer(JSON.parse(userData));
    } else {
      navigate('/portal-cliente/login');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axiosInstance.get('/client-portal/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_portal_token');
    localStorage.removeItem('client_portal_user');
    sessionStorage.removeItem('client_portal_token');
    sessionStorage.removeItem('client_portal_user');
    toast.success('Sesión cerrada');
    navigate('/portal-cliente/login');
  };

  const dashboardCards = [
    {
      title: 'Mis Facturas',
      description: 'Consulta y descarga tus facturas',
      icon: FileText,
      color: 'bg-blue-500',
      count: stats.invoices,
      path: '/portal-cliente/facturas'
    },
    {
      title: 'Mis Tickets',
      description: 'Gestiona tus tickets de soporte',
      icon: Ticket,
      color: 'bg-green-500',
      count: stats.tickets,
      path: '/portal-cliente/tickets'
    },
    {
      title: 'Mis Presupuestos',
      description: 'Revisa y aprueba presupuestos',
      icon: FileSpreadsheet,
      color: 'bg-orange-500',
      count: stats.quotes,
      badge: stats.pending_quotes > 0 ? `${stats.pending_quotes} pendientes` : null,
      path: '/portal-cliente/presupuestos'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#9C0F17] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white rounded-full p-2">
                <User className="w-6 h-6 text-[#9C0F17]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Portal del Cliente</h1>
                {customer && (
                  <p className="text-white/90 text-sm">{customer.name}</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="bg-white text-[#9C0F17] hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            ¡Bienvenido, {customer?.name}!
          </h2>
          <p className="text-gray-600">
            Accede a toda tu información desde un solo lugar
          </p>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {dashboardCards.map((card, index) => (
            <Card
              key={index}
              className="hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => navigate(card.path)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className={`${card.color} p-3 rounded-lg`}>
                    <card.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-900">{card.count}</p>
                    {card.badge && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                        {card.badge}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardTitle className="mb-2">{card.title}</CardTitle>
                <CardDescription>{card.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Customer Logo Section */}
        {customer?.logo_url && (
          <div className="mt-12 text-center">
            <div className="inline-block bg-white rounded-lg shadow-md p-6">
              <img
                src={customer.logo_url}
                alt={customer.name}
                className="max-h-20 mx-auto"
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
